import { FC, useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { LoadingWrapper } from "@components";
import { useProperty } from "@services";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import CloseSquare from "@assets/svg/close-square.svg?react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Placeholder image for properties
const PropertyPlaceholder =
  "https://www.figma.com/api/mcp/asset/c4559f2f-2822-40a8-8aee-41da818908ec";

interface IProps {
  hotelId: string;
  onSelect: (subHotelId: string) => void;
}

interface BookingProperty {
  id: string;
  title: string;
  connected: boolean;
}

const BookingPropertyList: FC<IProps> = ({ hotelId, onSelect }) => {
  const { t } = useTranslation();
  const { showError } = useContext(ToastMessageContext);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const { GetBookingPropertiesForSeparatedMutation } = useProperty();

  const { mutate, isPending, data, isError, error } =
    GetBookingPropertiesForSeparatedMutation({
      onSuccess: () => {},
      onError: (e: any) => {
        const errorMessage =
          e?.response?.data?.errorMessages?.[0]?.messageEn ||
          e?.response?.data?.errorMessages?.[0]?.messageAr ||
          t("BOOKING.ERROR_FETCHING_PROPERTIES");
        showError(errorMessage);
      },
    });

  useEffect(() => {
    if (hotelId) {
      mutate(hotelId);
    }
  }, [hotelId, mutate]);

  const properties: BookingProperty[] = data?.result || [];

  const handlePropertySelect = (propertyId: string) => {
    // Toggle selection: if already selected, unselect it
    if (selectedPropertyId === propertyId) {
      setSelectedPropertyId("");
    } else {
      setSelectedPropertyId(propertyId);
    }
  };

  const handleContinue = () => {
    if (selectedPropertyId) {
      onSelect(selectedPropertyId);
    }
  };

  return (
    <LoadingWrapper loading={isPending}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: 4,
        }}
      >
        {/* Content Section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
          {/* Title and Description */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              sx={{
                fontFamily: "Tajawal",
                fontSize: "20px",
                fontWeight: 400,
                lineHeight: "20px",
                color: "#0F172A",
                textAlign: "right",
              }}
            >
              {t("BOOKING.ENTER_PROPERTY_ID_TITLE")}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Tajawal",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "16px",
                color: "#8E98A1",
                textAlign: "right",
              }}
            >
              {t("BOOKING.ENTER_PROPERTY_ID_DESC")}
            </Typography>
          </Box>

          {/* Hotel ID Display */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "319px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Tajawal",
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: "24px",
                color: "#331860",
                textAlign: "right",
              }}
            >
              {t("BOOKING.HOTEL_ID")}
            </Typography>
            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "4px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "#FFFFFF",
              }}
            >
              <CloseSquare style={{ width: "22px", height: "22px" }} />
              <Typography
                sx={{
                  fontFamily: "Tajawal",
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: "24px",
                  color: "#8E98A1",
                  flex: 1,
                  textAlign: "right",
                }}
              >
                {hotelId}
              </Typography>
            </Box>
          </Box>

          {/* Properties Found Message */}
          {properties.length > 0 && (
            <Typography
              sx={{
                fontFamily: "IBM Plex Sans Arabic",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                color: "#666666",
                textAlign: "right",
              }}
            >
              {t("BOOKING.PROPERTIES_FOUND", { count: properties.length })}
            </Typography>
          )}

          {/* Property Cards Grid */}
          {properties.length > 0 && (
            <RadioGroup
              value={selectedPropertyId}
              onChange={(e) => handlePropertySelect(e.target.value)}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  justifyContent: "flex-end",
                }}
              >
                {properties.map((property) => (
                  <FormControlLabel
                    key={property.id}
                    value={property.id}
                    control={<Radio sx={{ display: "none" }} />}
                    disabled={property.connected}
                    label={
                      <Box
                        onClick={(e) => {
                          if (!property.connected) {
                            e.preventDefault();
                            handlePropertySelect(property.id);
                          }
                        }}
                        sx={{
                          width: "200px",
                          minHeight: "200px", // Minimum height for all cards
                          padding: 1,
                          border:
                            property.connected ||
                            selectedPropertyId === property.id
                              ? "2px solid #5B3FFF"
                              : "2px solid #E5E7EB",
                          borderRadius: "16px",
                          backgroundColor:
                            property.connected ||
                            selectedPropertyId === property.id
                              ? "#EEEBFF"
                              : "transparent",
                          cursor: property.connected
                            ? "not-allowed"
                            : "pointer",
                          transition: "all 0.2s",
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          "&:hover": {
                            borderColor: property.connected
                              ? "#5B3FFF"
                              : "#5B3FFF",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            flex: 1,
                          }}
                        >
                          {/* Property Image */}
                          <Box
                            sx={{
                              width: "100%",
                              flexShrink: 0,
                              borderRadius: "8px",
                              overflow: "hidden",
                              position: "relative",
                              display: "flex",
                              justifyContent: "center",
                              mb: 2,
                            }}
                          >
                            <div className="rounded-full px-5 py-4 bg-[#f2f1fc]  flex items-center justify-center">
                              <g
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="32"
                                  height="32"
                                  viewBox="0 0 32 32"
                                  fill="none"
                                  style={{ display: "block", margin: "0 auto" }}
                                >
                                  <path
                                    d="M29.3334 29.3334H2.66669"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M22.6666 29.3333V7.99996C22.6666 5.4858 22.6666 4.22872 21.8856 3.44768C21.1045 2.66663 19.8474 2.66663 17.3333 2.66663H14.6666C12.1525 2.66663 10.8954 2.66663 10.1144 3.44768C9.33331 4.22872 9.33331 5.4858 9.33331 7.99996V29.3333"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                  />
                                  <path
                                    d="M28 29.3333V11.3333C28 9.46068 28 8.52437 27.5505 7.85177C27.356 7.5606 27.106 7.3106 26.8148 7.11604C26.1422 6.66663 25.206 6.66663 23.3333 6.66663"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                  />
                                  <path
                                    d="M4 29.3333V11.3333C4 9.46068 4 8.52437 4.44941 7.85177C4.64397 7.5606 4.89397 7.3106 5.18515 7.11604C5.85775 6.66663 6.79405 6.66663 8.66667 6.66663"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                  />
                                  <path
                                    d="M16 29.3334V25.3334"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M13.3333 16H18.6666"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M7.33331 14.6666H9.33331"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M7.33331 18.6666H9.33331"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M22.6667 14.6666H24.6667"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M22.6667 18.6666H24.6667"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M7.33331 10.6666H9.33331"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M22.6667 10.6666H24.6667"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M13.3333 20H18.6666"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                  />
                                  <path
                                    d="M16 12C17.4727 12 18.6666 10.8061 18.6666 9.33329C18.6666 7.86053 17.4727 6.66663 16 6.66663C14.5272 6.66663 13.3333 7.86053 13.3333 9.33329C13.3333 10.8061 14.5272 12 16 12Z"
                                    stroke="#5B3FFF"
                                    stroke-width="1.5"
                                  />
                                </svg>
                              </g>
                            </div>
                          </Box>

                          {/* Property Info */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.75,
                              flex: 1,
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Tajawal",
                                fontSize: "14px",
                                fontWeight: 700,
                                lineHeight: "1.2",
                                color: "#331860",
                                textAlign: "right",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                minHeight: "34px", // Reserve space for 2 lines
                              }}
                            >
                              {property.title}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Tajawal",
                                fontSize: "12px",
                                fontWeight: 400,
                                lineHeight: "normal",
                                color: "#625783",
                                textAlign: "right",
                              }}
                            >
                              {t("BOOKING.PROPERTY_ID")}: {property.id}
                            </Typography>
                          </Box>

                          {/* Checkmark Icon for Connected or Selected */}
                          {(property.connected ||
                            selectedPropertyId === property.id) && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "24px", // Reserve space for the icon
                                flexShrink: 0,
                              }}
                            >
                              <CheckCircleIcon
                                sx={{
                                  color: "#5B3FFF",
                                  fontSize: "18px",
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    }
                    sx={{ margin: 0 }}
                  />
                ))}
              </Box>
            </RadioGroup>
          )}

          {/* No Properties Message */}
          {!isPending && properties.length === 0 && (
            <Typography
              sx={{
                fontFamily: "IBM Plex Sans Arabic",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                color: "#666666",
                textAlign: "center",
                marginTop: 4,
              }}
            >
              {t("BOOKING.NO_PROPERTIES_AVAILABLE")}
            </Typography>
          )}
        </Box>

        {/* Footer with Continue Button */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", paddingTop: 2 }}
        >
          <Button
            size="small"
            variant="contained"
            onClick={handleContinue}
            disabled={!selectedPropertyId}
          >
            {t("BOOKING.CONTINUE")}
          </Button>
        </Box>
      </Box>
    </LoadingWrapper>
  );
};

export default BookingPropertyList;
